/**
 * AgencyInsignia — chrome-bezeled mount for the agency patch / logo
 * in the AppShell top bar. Sits beside the EMSPCR wordmark.
 *
 * Each agency configures its own patch via the VITE_AGENCY_INSIGNIA_URL
 * and VITE_AGENCY_INSIGNIA_LABEL env vars. With no agency configured
 * (the independent-pilot default) the bezel renders a neutral EMS
 * monogram rather than any specific department's logo — EMSPCR.app
 * is not affiliated with any one agency.
 *
 * Dual-mode: v1 keeps the existing flat chrome bezel. v2 adds an
 * SVG-layered chrome rim (bright top-left highlight, dark bottom-
 * right shadow) that reads as a real machined bezel mounted into
 * the diamond-plate banner. Same dimensions in both so layouts
 * don't shift between themes.
 */

import { useState } from 'react';
import { useThemeStore } from '@/lib/theme-store';
import { cn } from '@/lib/cn';

// No agency-specific default. An agency sets its own patch through
// the env vars; absent that, the monogram fallback renders.
const INSIGNIA_URL =
  (import.meta.env.VITE_AGENCY_INSIGNIA_URL as string | undefined) ?? '';

const INSIGNIA_LABEL =
  (import.meta.env.VITE_AGENCY_INSIGNIA_LABEL as string | undefined) ?? 'Agency';

export function AgencyInsignia() {
  // Start in the errored (monogram) state when no URL is configured —
  // skips the broken <img> load entirely.
  const [errored, setErrored] = useState(!INSIGNIA_URL);
  const cockpitV2 = useThemeStore((s) => s.cockpitV2);

  const inner = errored ? (
    <span
      className={cn(
        'font-black tracking-widest select-none',
        cockpitV2 ? 'text-[8px] text-slate-800' : 'text-[8px] text-slate-700',
      )}
    >
      EMS
    </span>
  ) : (
    <img
      src={INSIGNIA_URL}
      alt={INSIGNIA_LABEL}
      onError={() => setErrored(true)}
      className="w-full h-full object-contain p-0.5"
      loading="lazy"
    />
  );

  if (!cockpitV2) {
    // v1 — flat chrome bezel, slate-200 fill behind the patch.
    return (
      <span
        className="insignia-bezel inline-flex items-center justify-center w-10 h-10 rounded-md overflow-hidden shrink-0 bg-slate-200"
        title={INSIGNIA_LABEL}
        aria-label={INSIGNIA_LABEL}
      >
        {inner}
      </span>
    );
  }

  // v2 — SVG-layered machined bezel. The bezel rim itself is an SVG
  // ring with a bright top-left → dark bottom-right gradient, and the
  // patch sits inside an inset white plate with a subtle inner shadow.
  // Same 40px outer dimension as v1 so layouts don't shift.
  return (
    <span
      className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: 40, height: 40 }}
      title={INSIGNIA_LABEL}
      aria-label={INSIGNIA_LABEL}
    >
      {/* Bezel rim — absolute SVG behind the patch */}
      <svg
        aria-hidden
        viewBox="0 0 40 40"
        className="absolute inset-0 w-full h-full"
      >
        <defs>
          <linearGradient id="insignia-rim" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="#f4f6f8" />
            <stop offset="45%"  stopColor="#aab2bc" />
            <stop offset="100%" stopColor="#4d5560" />
          </linearGradient>
          <linearGradient id="insignia-rim-inner" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="rgba(0, 0, 0, 0.35)" />
            <stop offset="50%"  stopColor="rgba(0, 0, 0, 0)" />
            <stop offset="100%" stopColor="rgba(255, 255, 255, 0.55)" />
          </linearGradient>
        </defs>
        {/* Outer rim — chrome ring */}
        <rect x="0" y="0" width="40" height="40" rx="6" fill="url(#insignia-rim)" />
        {/* Inset shadow ring — depth between rim and plate */}
        <rect
          x="2.5" y="2.5" width="35" height="35" rx="5"
          fill="url(#insignia-rim-inner)"
        />
        {/* Plate — pure-white display surface for the agency patch */}
        <rect
          x="3" y="3" width="34" height="34" rx="4"
          fill="#ffffff"
          stroke="rgba(0,0,0,0.50)" strokeWidth="0.6"
        />
      </svg>
      {/* Patch / monogram lives in an absolute layer on top */}
      <span className="absolute inset-[3px] rounded-[4px] flex items-center justify-center overflow-hidden">
        {inner}
      </span>
    </span>
  );
}
